import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '@/hooks/use-app-context';
import { COUNTRIES } from '@/lib/countries';
import { collectionPlaceErrorKey, createDeclaredPlace, isPlaceQuery } from './collection-place.contract';
import { collectionPlaceService } from './collection-place.service';
import { CityPicker, type CitySearchState } from './components/CityPicker';
import { GeolocationConsentPanel } from './components/GeolocationConsentPanel';
import { PlaceSummary } from './components/PlaceSummary';
import type { CollectionPlaceState } from './use-collection-place';
import { usePlaceSearch } from './use-place-search';

/** The "where are you collecting?" step every collection session starts with. */
export function CollectionPlaceStep({ state }: { state: CollectionPlaceState }) {
  const { t, user, language } = useApp();
  const copy = t.collectionPlace;
  const [query, setQuery] = useState('');
  const [countryCode, setCountryCode] = useState(() => state.place?.countryCode ?? profileCountryCode(user?.country));
  const search = usePlaceSearch(query);
  const results = search.data ?? [];

  const searchState: CitySearchState = !isPlaceQuery(query)
    ? 'idle'
    : !search.isSettled || search.isFetching
      ? 'loading'
      : search.isError
        ? 'error'
        : results.length === 0
          ? 'empty'
          : 'ready';
  const searchErrorKey = collectionPlaceErrorKey(isAxiosError(search.error) ? search.error.response?.status : undefined);
  const history = useQuery({
    queryKey: ['geo', 'declared-history', user?.id ?? 'anonymous'],
    queryFn: collectionPlaceService.recentDeclaredPlaces,
    enabled: Boolean(user?.id && isPlaceQuery(query) && search.isSettled && (search.isError || results.length === 0)),
    staleTime: 10 * 60_000,
    retry: false,
  });

  useEffect(() => {
    setCountryCode(state.place?.countryCode ?? profileCountryCode(user?.country));
  }, [state.place?.countryCode, user?.country, user?.id]);

  return (
    <section aria-labelledby="collection-place-title" className="space-y-3" data-testid="collection-place-step">
      <div>
        <h2 id="collection-place-title" className="text-sm font-semibold text-foreground">{copy.title}</h2>
        <p className="text-xs text-muted-foreground">{copy.subtitle}</p>
      </div>

      {state.status === 'asking' && (
        <GeolocationConsentPanel copy={copy} isSaving={state.isSavingConsent} onAnswer={state.answerConsent} />
      )}

      {state.status === 'locating' && (
        <p role="status" className="animate-pulse rounded-2xl border border-border/45 px-4 py-3 text-sm text-muted-foreground">
          {copy.locating}
        </p>
      )}

      {state.status === 'ready' && state.place && <PlaceSummary copy={copy} place={state.place} onChange={state.change} />}

      {state.status === 'manual' && (
        <>
          {!state.notice && <p className="text-xs text-muted-foreground">{copy.required}</p>}
          {state.notice && <p className="rounded-xl bg-muted/25 px-3 py-2 text-xs text-muted-foreground">{copy[state.notice]}</p>}
          {state.canUsePosition && (
            <button type="button" onClick={state.locate} className="text-xs font-semibold text-primary hover:text-primary/80">
              {copy.useMyPosition}
            </button>
          )}
          <CityPicker
            copy={copy}
            query={query}
            state={searchState}
            errorKey={searchErrorKey}
            results={results}
            previousPlaces={history.data ?? []}
            isLoadingPreviousPlaces={history.isFetching}
            countryCode={countryCode}
            language={language === 'en' ? 'en' : 'fr'}
            onQueryChange={setQuery}
            onCountryChange={setCountryCode}
            onSelect={state.choose}
            onUseTyped={(country) => {
              const place = createDeclaredPlace(query, country, language === 'en' ? 'en' : 'fr');
              if (place) state.choose(place);
            }}
          />
        </>
      )}
    </section>
  );
}

function profileCountryCode(value: string | undefined): string {
  const normalized = value?.trim().toLocaleLowerCase();
  return COUNTRIES.find((country) => country.iso.toLocaleLowerCase() === normalized || country.name.toLocaleLowerCase() === normalized)?.iso ?? '';
}
